import { ENDPOINT } from '~/constants';

import {
  buildResponseValidationReport,
  type KyRequestOptions,
} from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type InvoiceWatermarksResponse } from './types';
import { zodInvoiceWatermarksResponseSchema } from './validations';

/**
 * Fetches the invoice watermark names grouped by color.
 *
 * @param options - Additional request options (e.g., abort signal).
 * @returns The invoice watermark names grouped by color.
 *
 * @see https://app.dev.vestigas.com/redoc
 */
export async function fetchInvoiceWatermarks(options?: KyRequestOptions) {
  try {
    const response = await vestigasApi
      .get(ENDPOINT.INVOICE_REPROCESSING.GET_WATERMARKS(), {
        ...options,
      })
      .json<unknown>();
    const parsed = zodInvoiceWatermarksResponseSchema.safeParse(response);

    if (!parsed.success) {
      log.responseValidation({
        ...buildResponseValidationReport(parsed.error.issues),
        key: 'fetch-invoice-watermarks',
        message: 'fetch invoice watermarks response has validation errors',
      });

      throw new Error(
        'Fetch invoice watermarks response did not match the expected shape',
      );
    }

    return parsed.data satisfies InvoiceWatermarksResponse;
  } catch (error) {
    log.error({
      message: 'Error fetching invoice watermarks',
      error,
    });

    if (error instanceof HTTPError) {
      ToastService.error(
        i18n.t(
          ($) =>
            $.invoiceReprocessingQueries.fetchInvoiceWatermarks.error.generic,
          { ns: 'data' },
        ),
      );
    }

    throw error;
  }
}
